import React from 'react';
import toast from 'react-hot-toast';

const SIGNABLE_ATTACHMENT_STATUSES = ['IN_PROGRESS', 'RETURNED', 'ASSIGNED'];

const PRIVILEGED_SIGN_ROLES = ['ADMIN', 'SUPERADMIN'];

/** Załączniki z KSeF i pracownicy sekretariatu nie wymagają wewnętrznego podpisu. */
export function isAttachmentSigningRequired(attachment, user) {
  if (attachment?.source === 'KSEF') {
    return false;
  }
  if (user?.role === 'SEKRETARIAT') {
    return false;
  }
  return true;
}

export function isAttachmentSigningRequiredForUser(user) {
  return user?.role !== 'SEKRETARIAT';
}

/**
 * Czy użytkownik może podpisać załączniki na fakturze (ta sama reguła co backend).
 * @param {object} invoice
 * @param {object} user
 * @param {Array<{ managerId?: number }>} activeSubstitutions opcjonalna lista aktywnych zastępstw (zastępca = bieżący user)
 */
export function canSignAttachmentsOnInvoice(invoice, user, activeSubstitutions = []) {
  if (!invoice || !user) {
    return false;
  }
  if (!isAttachmentSigningRequiredForUser(user)) {
    return false;
  }
  if (PRIVILEGED_SIGN_ROLES.includes(user.role)) {
    return true;
  }
  if (!SIGNABLE_ATTACHMENT_STATUSES.includes(invoice.status)) {
    return false;
  }
  const assignedId = invoice.assignedUserId != null ? Number(invoice.assignedUserId) : null;
  const userId = user.id != null ? Number(user.id) : null;
  if (assignedId != null && userId != null && assignedId === userId) {
    return true;
  }
  if (assignedId != null && activeSubstitutions?.length) {
    return activeSubstitutions.some(
      (s) => s.managerId != null && Number(s.managerId) === assignedId
    );
  }
  return false;
}

/** Czy użytkownik może podpisać konkretny załącznik (np. KSeF jest wyłączony z podpisu). */
export function canSignAttachment(attachment, invoice, user, activeSubstitutions = []) {
  if (!isAttachmentSigningRequired(attachment, user)) {
    return false;
  }
  return canSignAttachmentsOnInvoice(invoice, user, activeSubstitutions);
}

/** Podpisy aktywne (nieskreślone) — do okna podpisywania / opisu faktury. */
export function filterActiveSignatures(signatures) {
  return (signatures || []).filter((sig) => sig.crossedOut !== true);
}

/** Czy załącznik ma aktywny (nieskreślony) podpis wewnętrzny w liście podpisów faktury. */
export function isAttachmentSigned(signatures, attachmentId) {
  const attId = attachmentId != null ? Number(attachmentId) : null;
  if (attId == null) {
    return false;
  }
  return (signatures || []).some((sig) => {
    if (sig.crossedOut === true) {
      return false;
    }
    const sigAttId = sig.attachmentId != null ? Number(sig.attachmentId) : null;
    return sigAttId !== null && sigAttId === attId;
  });
}

/** Nazwa załącznika do pokazania użytkownikowi. */
export function attachmentDisplayName(attachment) {
  return attachment?.originalFileName || attachment?.fileName || `Załącznik #${attachment?.id}`;
}

/**
 * Pobiera z API załączniki faktury, które blokują przekazanie/podpis dla bieżącego użytkownika.
 * Blokują te bez podpisu elektronicznego i bez oświadczenia bieżącego użytkownika,
 * że podpisuje je kto inny (`signWaivedByMe` z backendu — źródło prawdy: serwer).
 * @param {object} user bieżący użytkownik — sekretariat nie wymaga podpisu załączników
 * @returns {Promise<Array<{ id: number, name: string }>>}
 */
export async function fetchBlockingAttachments(api, invoiceId, user) {
  const [attRes, sigRes] = await Promise.all([
    api.get(`/invoices/${invoiceId}/attachments`, { timeout: 10000 }),
    api.get(`/documents/invoices/${invoiceId}/signatures`, { timeout: 10000 }),
  ]);
  const attachments = attRes.data || [];
  const signatures = sigRes.data || [];
  return attachments
    .filter((att) => isAttachmentSigningRequired(att, user))
    .filter((att) => att.signWaivedByMe !== true)
    .filter((att) => {
      const attId = att.id != null ? Number(att.id) : null;
      return !signatures.some((sig) => {
        if (sig.crossedOut === true) {
          return false;
        }
        const sigAttId = sig.attachmentId != null ? Number(sig.attachmentId) : null;
        return sigAttId !== null && attId !== null && sigAttId === attId;
      });
    })
    .map((att) => ({ id: att.id, name: attachmentDisplayName(att) }));
}

/**
 * Same nazwy blokujących załączników — cienka nakładka na {@link fetchBlockingAttachments}
 * dla miejsc, które potrzebują tylko komunikatu.
 */
export async function fetchUnsignedAttachmentNames(api, invoiceId, user) {
  const blocking = await fetchBlockingAttachments(api, invoiceId, user);
  return blocking.map((att) => att.name);
}

export function unsignedAttachmentsToast(names) {
  const list = names?.length ? ` (${names.join(', ')})` : '';
  return `Najpierw podpisz wszystkie załączniki${list} przed kontynuowaniem.`;
}

export function invoiceDisplayLabel(invoice) {
  if (!invoice) return '';
  const number = invoice.number || (invoice.id != null ? `ID ${invoice.id}` : 'faktura');
  if (invoice.internalNumber) {
    return `${number} (nr wew. ${invoice.internalNumber})`;
  }
  return number;
}

/**
 * Toast przy blokadzie przekazania: numer faktury + klik „Przejdź do tej faktury”.
 */
export function showUnsignedAttachmentBlockingToast({ invoice, names, onGoTo } = {}) {
  const label = invoiceDisplayLabel(invoice);
  const files = names?.length ? names.join(', ') : 'załącznik';
  toast.error(
    (t) => (
      <div className="text-sm leading-snug max-w-sm">
        <div>
          {label ? (
            <>
              Nie można przekazać faktury <strong>{label}</strong>.
            </>
          ) : (
            'Nie można przekazać faktury.'
          )}{' '}
          Niepodpisany załącznik: {files}.
        </div>
        {typeof onGoTo === 'function' && (
          <button
            type="button"
            className="mt-2 inline-flex items-center font-semibold underline underline-offset-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toast.dismiss(t.id);
              onGoTo();
            }}
          >
            Przejdź do tej faktury
          </button>
        )}
      </div>
    ),
    { duration: 16000 }
  );
}

/**
 * Faktura z odznaką NOWA: ostatnie przekazanie było z sekretariatu do bieżącego użytkownika.
 * Pierwszy odbiorca może odesłać ją dalej nawet z niepodpisanym załącznikiem.
 * Kolejna osoba w obiegu już musi podpisać albo wskazać kogoś.
 */
export function allowsForwardingWithUnsignedAttachments(invoice) {
  return invoice?.newFromSecretariat === true;
}

/** Czy trzeba najpierw przyjąć fakturę, zanim można wskazać osobę do podpisu załącznika. */
export function requiresAcceptBeforeAttachmentWaiver(invoice) {
  if (allowsForwardingWithUnsignedAttachments(invoice)) {
    return false;
  }
  const status = invoice?.status ? String(invoice.status).toUpperCase().trim() : '';
  return status === 'ASSIGNED';
}
